import FilterListIcon from "@mui/icons-material/FilterList";
import { useTheme } from "@mui/material";
import { capitalize, isEmpty, startCase, truncate } from "lodash";
import _ from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";

import { Box } from "@/Components/v2/atoms/Box";
import { OutlinedButton, ToggleButton } from "@/Components/v2/atoms/Button";
import { Divider } from "@/Components/v2/atoms/Divider";
import { Menu } from "@/Components/v2/atoms/Menu";
import { CircularProgress } from "@/Components/v2/atoms/Progress";
import RoundedBorderContainer from "@/Components/v2/atoms/RoundedBorderContainer/RoundedBorderContainer";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Typography } from "@/Components/v2/atoms/Typography";
import { BarChart } from "@/Components/v2/molecule/BarChart/BarChart";
import { ToggleButtonGroup } from "@/Components/v2/molecule/ButtonGroup";
import { LegendItem } from "@/Components/v2/molecule/LegendItem/LegendItem";
import Header from "@/Components/v2/organism/Header/Header";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useConfig } from "@/hooks/useConfig";
import { ABBREVIATED_OPTION } from "@/hooks/useConfig/currencyFormatOptions";
import { ONE_DECIMAL_OPTION } from "@/hooks/useConfig/numberAbbreviatorOptions";
import { useModuleName } from "@/hooks/useModuleName";
import { FilterSection, Toggle, ToolBarShared } from "@/shared/components/DataGrid/ToolBarShared";
import DataGridContainer from "@/shared/components/DataGridContainer";
import { useDateRangeStore } from "@/shared/components/date-range/store/useDateRangeStore";
import Title from "@/shared/components/Sections/Title";
import { BusinessUnitToggle, GeoViewToggle, MetricType } from "@/shared/types";
import Palette from "@/Utils/Colors/palette";
import { formatUnitValue } from "@/Utils/formatters";
import { Layer } from "@/Utils/hierarchy";

import { useSalesCompositionV2 } from "./api/getSalesCompositionV2";
import FilterComponent from "./components/FilterComponent";
import { getAllActiveColumns, getSalesDriversColumnDefinitions } from "./getColumnDefinitions";
import { getTooltip as getSalesCompositionTooltip } from "./helpers/salesCompositionChartTooltip";
import { getSalesFactorTooltip } from "./helpers/salesFactorsMap";
import { useSalesCompositionChartOptions } from "./hooks/useSalesCompositionChartOptions";
import { useSalesDriversStore } from "./store/useSalesDriversStore";
import { useAvailableFilters } from "./useAvailableFilters";

export const content = {
  title: "Sales Drivers",
  subtitle:
    "See what channels are driving sales and how much your brand is reliant on Pricing, Discounting or Marketing compared to Base Sales.",
};

const SalesDriversV2 = () => {
  const { config } = useConfig();

  const apiRef = useRef(null);
  const theme = useTheme();
  const palette = new Palette(theme.palette.ROILineChart.barChartColors);
  const { initInfo: info, loading: areGeosLoading, nodes } = useInfo();
  const { moduleOpened, unitsSelected } = useAnalytics();
  const { moduleName } = useModuleName();

  const { formatCurrency } = config.currency;
  const { formatAbbreviatedNumber } = config.numberAbbreviator;
  const currencySymbol = config.currency.getSymbol();

  const cmsConfig = config?.graphs?.SalesByComposition;
  const showSalesUnits = cmsConfig?.showSalesUnits;
  const salesUnitsLabel = cmsConfig?.salesUnitsLabel;
  const { showReturnInsteadOfRevenue } = config;

  const {
    filters: { targetNodeUUID: selectedNodeUUID, activeFilterIds },
    setTargetNode,
    filters,
    chart: { period: salesPeriod },
    grid: { businessUnitToggle, geoView, activeColumns, metricType },
    setBusinessUnitToggle,
    setGeoView,
    setActiveColumns,
    setMetricType,
    setDurationSwitch,
    setActiveFilterIds,
    setExpandedSectionIds,
  } = useSalesDriversStore();

  useEffect(() => {
    if (showReturnInsteadOfRevenue) {
      setMetricType(MetricType.SALES_UNITS);
    }
  }, []);

  const { startDate, endDate } = useDateRangeStore();
  const selectedNode = nodes?.getNode(selectedNodeUUID ?? "");

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const filterOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (nodes && selectedNodeUUID === null) {
      const defaultNodeUUID = nodes?.getDefaultNode()?.node.uuid;
      if (defaultNodeUUID) {
        setTargetNode(defaultNodeUUID);
      }
    }
  }, [nodes, setTargetNode, selectedNodeUUID]);

  const availableFilters = useAvailableFilters();

  // Get geography data for conditional logic
  const [geoData] = info?.geographies || [];
  const hasGeoData = geoData && geoData.length > 0;
  const geography = filters?.geography ?? [];

  const { data: salesCompositionDataV2, isLoading: isLoadingSalesCompV2 } = useSalesCompositionV2({
    request: {
      startDate: startDate || "",
      endDate: endDate || "",
      brand: selectedNode?.node.brand || "default",
      category: selectedNode?.node.category || "default",
      product: selectedNode?.node.product || "",
      salesPeriod,
      ...(hasGeoData ? { geographies: geography } : {}),
      geoViewToggle: geoView,
      businessUnitToggle: businessUnitToggle,
    },
    config: {
      enabled: !!selectedNode && !!startDate && !!endDate,
    },
  });

  const {
    seriesData,
    axisLabels,
    totalValues,
    filterLegendItems,
    legendHiddenCount,
    isGroupedLegend,
    groupedLegendItems,
    individualLegendItems,
  } = useSalesCompositionChartOptions({
    salesCompositionData: salesCompositionDataV2?.chartView,
    isDollarViewSelected: metricType === MetricType.SALES_REVENUE,
    formatCurrency: config.currency.formatCurrency,
    activeFilterIds,
    palette,
    theme,
  });

  const salesCompositionTooltipFormatter = (value: number, showUnitsString = false) => {
    if (metricType === MetricType.SALES_REVENUE) {
      return formatCurrency(value);
    }
    return `${formatUnitValue(value)}${showUnitsString ? ` ${salesUnitsLabel || "units"}` : ""}`;
  };

  const salesCompositionYAxisFormatter = (value: number) => {
    if (metricType === MetricType.SALES_REVENUE) {
      return formatCurrency(value, ABBREVIATED_OPTION);
    }
    return formatAbbreviatedNumber(value, ONE_DECIMAL_OPTION);
  };

  useEffect(() => {
    if (salesCompositionDataV2?.chartView?.y_axis && isEmpty(activeFilterIds)) {
      const allItems = Object.values(salesCompositionDataV2.chartView.y_axis).flatMap((section) =>
        Object.keys(section)
      );
      setActiveFilterIds(allItems);

      const allSections = Object.keys(salesCompositionDataV2.chartView.y_axis);
      setExpandedSectionIds(allSections);
    }
  }, [salesCompositionDataV2?.chartView, setActiveFilterIds, setExpandedSectionIds]);

  const isLoading =
    nodes === null || selectedNodeUUID === null || isLoadingSalesCompV2 || areGeosLoading;

  // Use the column definitions with dynamic factors
  const columns = getSalesDriversColumnDefinitions(
    salesCompositionDataV2?.tableView || [],
    activeColumns,
    {
      currencySymbol,
      formatCurrency,
      metricType,
      geoViewToggle: geoView,
      businessUnitToggle: businessUnitToggle,
    }
  );

  // Generate dynamic active columns based on available data and metric type
  const dynamicActiveColumns = getAllActiveColumns(
    salesCompositionDataV2?.tableView || [],
    metricType,
    geoView,
    businessUnitToggle
  );

  // Update active columns when data changes or metric type changes
  useEffect(() => {
    if (salesCompositionDataV2?.tableView && dynamicActiveColumns.length > 0) {
      setActiveColumns(dynamicActiveColumns);
    }
  }, [
    salesCompositionDataV2?.tableView,
    metricType,
    geoView,
    businessUnitToggle,
    setActiveColumns,
  ]);

  // Map columns to filter sections for grouped table filter
  const tableFilterSections = useMemo<FilterSection[]>(() => {
    const apiGrouping = salesCompositionDataV2?.chartView?.y_axis;
    if (!apiGrouping) return [];

    const sections: FilterSection[] = [];

    const metadataFields = ["period_start", "geography", "product_hierarchy"];
    const metadataColumns = columns.filter((col) => metadataFields.includes(col.field));

    if (metadataColumns.length > 0) {
      sections.push({
        id: "metadata",
        title: "Metadata",
        items: metadataColumns.map((col) => ({
          id: col.field,
          title: col.headerName || col.field,
          color: "#666",
          isActive: activeColumns.includes(col.field),
        })),
      });
    }

    Object.entries(apiGrouping).forEach(([sectionId, sectionData]) => {
      const variableNames = Object.keys(sectionData);

      const sectionColumns = columns.filter((col) => {
        const baseField = col.field.replace(/_sales$|_units$/, "");
        return variableNames.includes(baseField);
      });

      if (sectionColumns.length > 0) {
        sections.push({
          id: sectionId,
          title: startCase(sectionId),
          items: sectionColumns.map((col) => ({
            id: col.field,
            title: col.headerName || col.field,
            color: "#666",
            isActive: activeColumns.includes(col.field),
          })),
        });
      }
    });

    return sections;
  }, [columns, activeColumns, salesCompositionDataV2]);

  const toggles: Toggle[] = [
    {
      id: "businessUnitToggle",
      selectedValue: businessUnitToggle,
      options: [
        { label: "Aggregate Business Units", value: BusinessUnitToggle.AGGREGATE },
        { label: "Split by Business Unit", value: BusinessUnitToggle.SPLIT },
      ],
    },
  ];

  if (geoData && geoData.length > 0) {
    toggles.push({
      id: "geoViewToggle",
      selectedValue: geoView,
      options: [
        { label: "Aggregate Geographies", value: GeoViewToggle.AGGREGATE },
        { label: "Split by Geography", value: GeoViewToggle.SPLIT },
      ],
    });
  }

  const metricDropdownConfig = {
    label: "Show",
    value: metricType,
    options: [
      { label: "Sales Revenue", value: MetricType.SALES_REVENUE },
      { label: "Sales Units", value: MetricType.SALES_UNITS },
    ],
    onChange: (value: string | null) => {
      if (value) {
        setMetricType(value as MetricType);
      }
    },
  };

  const unitsRevenueToggleOptions = [
    { label: capitalize(salesUnitsLabel) || "Units", value: MetricType.SALES_UNITS },
    { label: "Revenue", value: MetricType.SALES_REVENUE },
  ];

  const salesPeriodToggleOptions = [
    { value: "month", label: "Months" },
    { value: "week", label: "Weeks" },
  ];

  useEffect(() => {
    moduleOpened("sales-drivers", Layer.Portfolio);
  }, []);

  return (
    <Box>
      <Title title={content.title} subtitle={content.subtitle} />
      <Header availableFilters={availableFilters} />
      {isLoading ? (
        <Stack justifyContent="center" alignItems="center" sx={{ minHeight: "50rem" }}>
          <CircularProgress size={150} />
        </Stack>
      ) : (
        <Box mx={4}>
          <RoundedBorderContainer>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h4" fontWeight={600} fontSize={24}>
                Sales Composition
              </Typography>
              <Stack direction="row" gap={2}>
                {showSalesUnits && !showReturnInsteadOfRevenue && (
                  <ToggleButtonGroup exclusive value={metricType} sx={{ width: 150 }}>
                    {unitsRevenueToggleOptions.map((option) => (
                      <ToggleButton
                        key={option.value}
                        value={option.value}
                        onClick={() => {
                          setMetricType(option.value as MetricType);
                          if (option.value === MetricType.SALES_UNITS) {
                            unitsSelected({ moduleName });
                          }
                        }}
                      >
                        {option.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                )}
                <ToggleButtonGroup value={salesPeriod} exclusive sx={{ width: 150 }}>
                  {salesPeriodToggleOptions.map((option) => (
                    <ToggleButton
                      key={option.label}
                      value={option.value}
                      onChange={(_, value) => setDurationSwitch(value)}
                    >
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <OutlinedButton
                  aria-controls={filterOpen ? "simple-menu" : undefined}
                  aria-haspopup="true"
                  onClick={handleClick}
                  variant="outlined"
                  sx={{
                    border: "none",
                    "&:hover": { border: "none", backgroundColor: "transparent" },
                  }}
                  startIcon={<FilterListIcon />}
                >
                  Filter
                </OutlinedButton>
                <Menu
                  id="simple-menu"
                  anchorEl={anchorEl}
                  open={filterOpen}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  PaperProps={{
                    style: {
                      maxHeight: "80vh",
                      width: 400,
                    },
                  }}
                  sx={{
                    "& .MuiList-root": {
                      paddingBottom: 0,
                    },
                  }}
                >
                  <FilterComponent sections={filterLegendItems} />
                </Menu>
              </Stack>
            </Stack>
            <Divider sx={{ my: 4 }} />
            <BarChart
              axisLabels={axisLabels}
              series={seriesData}
              yAxisLabelFormatter={salesCompositionYAxisFormatter}
              xAxisLabelFormatter={(value: string) => truncate(value, { length: 24 })}
              tooltipOptions={getSalesCompositionTooltip(
                totalValues,
                seriesData,
                salesCompositionTooltipFormatter,
                theme
              )}
              stacked
            />
            {seriesData.length > 0 && (
              <Stack direction="row" gap={2} pl={3} mt={2} alignItems="center" flexWrap="wrap">
                {isGroupedLegend ? (
                  groupedLegendItems.map((item) => (
                    <LegendItem
                      key={item.id}
                      item={{
                        id: item.id,
                        title: item.title,
                        color: item.color,
                        isActive: item.isActive,
                      }}
                      handleClick={() => {
                        const section = filterLegendItems.find((section) => section.id === item.id);
                        if (section) {
                          setActiveFilterIds(section.items.map((i) => i.id));
                          setExpandedSectionIds([section.id]);
                        } else {
                          setActiveFilterIds([item.id]);
                        }
                      }}
                      tooltip={getSalesFactorTooltip(item.title)}
                    />
                  ))
                ) : (
                  <>
                    {individualLegendItems.map((item) => (
                      <LegendItem
                        key={item.id}
                        item={{
                          id: item.id,
                          title: item.title,
                          color: item.color,
                          isActive: item.isActive,
                        }}
                        handleClick={() => {
                          setActiveFilterIds([item.id]);
                          setExpandedSectionIds([item.section]);
                        }}
                        tooltip={getSalesFactorTooltip(item.title)}
                      />
                    ))}
                  </>
                )}
                {legendHiddenCount > 0 && (
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    + {legendHiddenCount} Hidden
                  </Typography>
                )}
              </Stack>
            )}
          </RoundedBorderContainer>

          <DataGridContainer
            apiRef={apiRef}
            rows={salesCompositionDataV2?.tableView || []}
            columns={columns}
            metricDropdown={showReturnInsteadOfRevenue ? undefined : metricDropdownConfig}
            toolbarComponent={
              <ToolBarShared
                apiRef={apiRef}
                columns={columns}
                activeColumns={activeColumns}
                toggles={toggles}
                handleApplyFilters={({ activeColumns, toggles }) => {
                  setActiveColumns(activeColumns);
                  const businessUnitToggleValue = toggles["businessUnitToggle"];
                  if (businessUnitToggleValue) {
                    setBusinessUnitToggle(businessUnitToggleValue as BusinessUnitToggle);
                  }
                  const geoViewToggleValue = toggles["geoViewToggle"];
                  if (geoViewToggleValue) {
                    setGeoView(geoViewToggleValue as GeoViewToggle);
                  }
                }}
                handleResetFilters={() => {
                  setActiveColumns(dynamicActiveColumns);
                  setBusinessUnitToggle(BusinessUnitToggle.AGGREGATE);
                  setGeoView(GeoViewToggle.AGGREGATE);
                }}
                useGroupedFilters={true}
                groupedFilterSections={tableFilterSections}
              />
            }
            options={{
              rowHeight: 52,
              sortModel: [{ field: "period_start", sort: "asc" }],
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default SalesDriversV2;
